/**
 * The journal panel (R16): every document the player has read, on the shelf.
 *
 * @remarks
 * Pure and prop-driven like every component here. It renders the engine's
 * {@link JournalEntry} derivation and computes nothing: `deriveJournal` is the
 * single source of truth for what has been read and what its lines say NOW
 * (readings are live — the journal is the player re-reading the document, and
 * a UI that stored its own copy of the text would be a second record beside
 * the transcript, drifting).
 *
 * Accessibility: an ordered list — read order is meaningful — with one
 * `<article>` per document named by its carrier; lines carry their transcript
 * source as a styling class exactly as transcript entries do, so a document's
 * human voice reads in the same register on the shelf as it did in the flow.
 *
 * @packageDocumentation
 */

import { type JournalEntryView } from '@orpheus/api-contracts';

import { sourceModifierOf } from '../revealed-text/reveal-cadence.js';
import { RevealedTextComponent } from '../revealed-text/revealed-text-component.js';

/** Props for {@link JournalPanelComponent}. */
export interface JournalPanelComponentProps {
	/** The engine's journal derivation — read documents, oldest first. */
	readonly entries: readonly JournalEntryView[];
}

/**
 * Render the journal panel: heading, then each read document in read order.
 *
 * @param props - {@link JournalPanelComponentProps}
 * @returns The rendered panel
 */
export const JournalPanelComponent = ({ entries }: JournalPanelComponentProps) => (
	<section aria-labelledby="journal-panel-heading" className="journal-panel">
		<h2 className="journal-panel__heading" id="journal-panel-heading">
			Journal — {entries.length} document{entries.length === 1 ? '' : 's'} read
		</h2>
		{entries.length === 0 ? (
			<p className="journal-panel__empty">Nothing collected yet. Documents you read are kept here.</p>
		) : (
			<ol className="journal-panel__documents">
				{entries.map((entry) => (
					<li className="journal-panel__document" key={entry.logId}>
						<article aria-label={entry.title}>
							<h3 className="journal-panel__document-title">{entry.title}</h3>
							{entry.foundInRoomName !== undefined && (
								// J2 (D80, Mike's ruling): one derived provenance line — the
								// shelf is the only place the documents sit side by side, and
								// WHERE each one lived is part of what they say together. The
								// article is Calder's D81 ruling: "Found in the Checkpoint" is
								// a person saying where they picked something up; without it,
								// the line reads like a label on a filing box. No terminal
								// period — a caption under a title, not a sentence.
								<p className="journal-panel__document-provenance">Found in the {entry.foundInRoomName}</p>
							)}
							{entry.lines.map((line, lineIndex) => (
								<p
									className={`journal-panel__line journal-panel__line--${sourceModifierOf(line.source)}`}
									// A page's position IS its identity: document lines are
									// authored in order and never reorder, so the index is a
									// stable key, not a smell.
									key={`page-${String(lineIndex)}`}
								>
									{/* D145: a found document can be redacted too — same bar, same
									    seam as the flow, so the token never reads literally anywhere
									    the eye is meant to see a bar. */}
									<RevealedTextComponent cadence="instant" text={line.text} />
								</p>
							))}
						</article>
					</li>
				))}
			</ol>
		)}
	</section>
);
